import type { ResolverConfigDto, ResolverNodeDto } from "../../../../../types/resolver";

const LAYOUT_HORZ_SPACING = 280;
const LAYOUT_VERT_SPACING = 100;

/** BFS from startId to get node order; fallback to config order. */
export function nodeOrderForLayout(config: ResolverConfigDto): ResolverNodeDto[] {
    const byId = new Map(config.nodes.map((n) => [n.id, n]));
    const start = byId.get(config.startNodeId);
    if (!start) {
        return config.nodes;
    }
    const order: ResolverNodeDto[] = [];
    const visited = new Set<number>();
    const queue: ResolverNodeDto[] = [start];
    while (queue.length > 0) {
        const node = queue.shift()!;
        if (visited.has(node.id)) continue;
        visited.add(node.id);
        order.push(node);
        for (const edge of node.edges ?? []) {
            const next = byId.get(edge.toNodeId);
            if (next && !visited.has(next.id)) queue.push(next);
        }
    }
    for (const n of config.nodes) {
        if (!visited.has(n.id)) order.push(n);
    }
    return order;
}

/** Compute depth (level) of each node from startNodeId via BFS. Unreached nodes get max level. */
export function nodeLevelsForLayout(config: ResolverConfigDto): Map<number, number> {
    const byId = new Map(config.nodes.map((n) => [n.id, n]));
    const levels = new Map<number, number>();
    const start = byId.get(config.startNodeId);
    if (!start) {
        config.nodes.forEach((n, i) => levels.set(n.id, i));
        return levels;
    }
    const queue: { node: ResolverNodeDto; level: number }[] = [{ node: start, level: 0 }];
    const visited = new Set<number>();
    while (queue.length > 0) {
        const { node, level } = queue.shift()!;
        if (visited.has(node.id)) continue;
        visited.add(node.id);
        levels.set(node.id, level);
        for (const edge of node.edges ?? []) {
            const next = byId.get(edge.toNodeId);
            if (next && !visited.has(next.id)) queue.push({ node: next, level: level + 1 });
        }
    }
    let fallbackLevel = levels.size;
    for (const n of config.nodes) {
        if (!levels.has(n.id)) levels.set(n.id, fallbackLevel++);
    }
    return levels;
}

/** Left-to-right tree layout: x by depth, y evenly spaced per level and centered within level. */
export function treeLayoutPositions(config: ResolverConfigDto): Map<number, { x: number; y: number }> {
    const levels = nodeLevelsForLayout(config);
    const byLevel = new Map<number, number[]>();
    for (const node of config.nodes) {
        const level = levels.get(node.id) ?? 0;
        if (!byLevel.has(level)) byLevel.set(level, []);
        byLevel.get(level)!.push(node.id);
    }
    const levelIndices = [...byLevel.keys()].sort((a, b) => a - b);
    const positions = new Map<number, { x: number; y: number }>();
    const baseY = 80;
    levelIndices.forEach((level) => {
        const nodeIds = byLevel.get(level)!;
        const count = nodeIds.length;
        const totalHeight = (count - 1) * LAYOUT_VERT_SPACING;
        const startY = baseY - totalHeight / 2;
        nodeIds.forEach((nodeId, indexInLevel) => {
            const x = 80 + level * LAYOUT_HORZ_SPACING;
            const y = startY + indexInLevel * LAYOUT_VERT_SPACING;
            positions.set(nodeId, { x, y });
        });
    });
    return positions;
}
